import { SOCIAL_POST_STATUS_LABELS, type SocialPostStatus } from "@dream/config/constants";
import {
  Badge,
  Button,
  Card,
  Dialog,
  EmptyState,
  Field,
  IconPublish,
  Input,
  Label,
  Select,
  Skeleton,
  Textarea,
  useToast,
} from "@dream/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { DateTime } from "luxon";
import { useMemo, useState } from "react";
import { ApiError, api, unwrap } from "../api/client.ts";
import { useAdminCatalog } from "../hooks/use-calendar.ts";

interface SettingOption {
  value: string;
  label: string;
}

interface SettingField {
  key: string;
  label: string;
  type: string;
  help?: string;
  placeholder?: string;
  required?: boolean;
  options?: SettingOption[];
  defaultValue?: unknown;
  maxLength?: number;
  when?: { field: string; equals: string | boolean | number };
}

interface ConnectField {
  key: string;
  label: string;
  type: string;
  required: boolean;
  placeholder?: string;
  help?: string;
}

interface Provider {
  id: string;
  name: string;
  color: string;
  charLimit: number;
  category: string;
  connectMethods: string[];
  oauthReady: boolean;
  needsDeveloperApp: boolean;
  credentialFields: ConnectField[];
  settings: SettingField[];
  media: { note: string; minAttachments: number; maxAttachments: number; textOnly: boolean };
  defaultSettings: Record<string, unknown>;
  defaultSlots: Array<{ weekday: number; time: string }>;
}

interface Account {
  id: string;
  clientId: string | null;
  platform: string;
  authMethod: string;
  displayName: string;
  handle: string | null;
  status: string;
  lastError: string | null;
  live: boolean;
  postingSlots: Array<{ weekday: number; time: string }>;
}

interface PostTarget {
  id: string;
  accountId: string;
  platform: string | null;
  displayName: string | null;
  status: string;
  remoteUrl: string | null;
  lastError: string | null;
}

interface SocialPost {
  id: string;
  title: string;
  globalContent: string;
  status: SocialPostStatus;
  scheduledAt: string | null;
  version: number;
  clientId: string;
  targets: PostTarget[];
  createdAt: string;
}

type Tab = "contas" | "compor" | "fila";

const CATEGORY_LABEL: Record<string, string> = {
  social: "Redes sociais",
  video: "Vídeo",
  community: "Comunidades",
  blog: "Blogs e newsletters",
  messaging: "Mensageiros",
  other: "Outros",
};

export function PublisherPage() {
  const catalog = useAdminCatalog();
  const toast = useToast();
  const queryClient = useQueryClient();
  const clients = catalog.clients as Array<{ id: string; name: string }>;
  const [clientId, setClientId] = useState<string>("");
  const [tab, setTab] = useState<Tab>("compor");

  const resolvedClientId = clientId === "" ? (clients[0]?.id ?? "") : clientId;

  const providers = useQuery({
    queryKey: ["social-providers"],
    queryFn: async () =>
      unwrap(await api.api.admin.social.providers.get()) as unknown as Provider[],
  });

  const accounts = useQuery({
    queryKey: ["social-accounts", resolvedClientId],
    enabled: resolvedClientId !== "",
    queryFn: async () =>
      unwrap(
        await api.api.admin.social.accounts.get({ query: { clientId: resolvedClientId } }),
      ) as unknown as Account[],
  });

  const posts = useQuery({
    queryKey: ["social-posts", resolvedClientId],
    enabled: resolvedClientId !== "",
    queryFn: async () =>
      unwrap(
        await api.api.admin.social.posts.get({ query: { clientId: resolvedClientId } }),
      ) as unknown as SocialPost[],
  });

  function invalidate(): void {
    void queryClient.invalidateQueries({ queryKey: ["social-accounts"] });
    void queryClient.invalidateQueries({ queryKey: ["social-posts"] });
  }

  return (
    <div className="dream-scroll min-h-0 flex-1 overflow-auto p-4 md:p-6">
      <div className="mx-auto flex max-w-6xl flex-col gap-4">
        <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
          <div>
            <h1 className="text-[1.25rem] font-bold leading-[1.35] tracking-[0] text-[var(--color-ink)] sm:text-[1.5rem]">
              Publicações
            </h1>
            <p className="mt-0.5 max-w-2xl text-[0.8125rem] leading-5 text-[var(--color-ink-muted)]">
              Conecte as 34 redes, escreva uma vez e publique com as configurações de cada
              plataforma. OAuth ao vivo quando o app da rede estiver no servidor; as demais redes
              entram com as credenciais da própria conta.
            </p>
          </div>
          <Field label="Cliente" htmlFor="publisher-client" className="md:w-72">
            <Select
              id="publisher-client"
              value={resolvedClientId}
              onChange={(event) => setClientId(event.target.value)}
              disabled={clients.length === 0}
            >
              {clients.map((client) => (
                <option key={client.id} value={client.id}>
                  {client.name}
                </option>
              ))}
            </Select>
          </Field>
        </div>

        <div className="flex h-12 gap-1 rounded-lg bg-[var(--color-surface-subtle)] p-1">
          {(
            [
              ["compor", "Compor"],
              ["contas", "Contas"],
              ["fila", "Fila"],
            ] as const
          ).map(([id, label]) => (
            <button
              key={id}
              type="button"
              onClick={() => setTab(id)}
              className={[
                "flex-1 rounded-md px-3 text-[14px] tracking-[0]",
                "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500",
                tab === id
                  ? "bg-[var(--color-surface)] font-bold text-[#0074FF] shadow-[0_1px_2px_rgb(0_0_0/0.06)]"
                  : "font-medium text-[var(--color-ink-muted)] hover:text-[var(--color-ink)]",
              ].join(" ")}
            >
              {label}
            </button>
          ))}
        </div>

        {clients.length === 0 ? (
          <EmptyState
            icon={<IconPublish className="size-5" />}
            title="Cadastre um cliente primeiro"
            description="As redes sociais pertencem a um cliente. Abra Clientes, crie a conta e volte para conectar Instagram, Bluesky e o restante."
          />
        ) : providers.isLoading || catalog.isLoading ? (
          <Skeleton className="h-64" />
        ) : providers.error instanceof ApiError ? (
          <EmptyState
            icon={<IconPublish className="size-5" />}
            title="Não foi possível carregar o postador"
            description={providers.error.message}
          />
        ) : tab === "contas" ? (
          <AccountsPanel
            providers={providers.data ?? []}
            accounts={accounts.data ?? []}
            clientId={resolvedClientId}
            onChanged={invalidate}
            toast={toast}
          />
        ) : tab === "fila" ? (
          <QueuePanel posts={posts.data ?? []} onChanged={invalidate} toast={toast} />
        ) : (
          <ComposerPanel
            providers={providers.data ?? []}
            accounts={(accounts.data ?? []).filter((a) => a.status === "active")}
            clientId={resolvedClientId}
            onPublished={() => {
              invalidate();
              setTab("fila");
            }}
            toast={toast}
          />
        )}
      </div>
    </div>
  );
}

function AccountsPanel({
  providers,
  accounts,
  clientId,
  onChanged,
  toast,
}: {
  providers: Provider[];
  accounts: Account[];
  clientId: string;
  onChanged: () => void;
  toast: ReturnType<typeof useToast>;
}) {
  const [connecting, setConnecting] = useState<Provider | null>(null);
  const byPlatform = new Map(
    accounts.filter((a) => a.status === "active").map((a) => [a.platform, a]),
  );

  const groups = useMemo(() => {
    const map = new Map<string, Provider[]>();
    for (const provider of providers) {
      const list = map.get(provider.category) ?? [];
      list.push(provider);
      map.set(provider.category, list);
    }
    return [...map.entries()];
  }, [providers]);

  return (
    <div className="space-y-5">
      <p className="text-[0.8125rem] text-[var(--color-ink-muted)]">
        {accounts.filter((a) => a.status === "active").length} de {providers.length} redes ativas
        neste cliente.
      </p>
      {groups.map(([category, list]) => (
        <section key={category}>
          <h2 className="mb-2 text-sm font-semibold text-[var(--color-ink)]">
            {CATEGORY_LABEL[category] ?? category}
          </h2>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {list.map((provider) => {
              const account = byPlatform.get(provider.id);
              return (
                <Card
                  key={provider.id}
                  className="flex items-center gap-3 p-3 shadow-[var(--shadow-xs)]"
                >
                  <span
                    className="size-9 shrink-0 rounded-lg"
                    style={{ backgroundColor: provider.color }}
                    aria-hidden="true"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <p className="truncate font-semibold text-[var(--color-ink)]">
                        {provider.name}
                      </p>
                      {account !== undefined ? (
                        <Badge tone={account.live ? "success" : "info"}>
                          {account.live ? "ao vivo" : account.authMethod}
                        </Badge>
                      ) : (
                        <Badge>desconectada</Badge>
                      )}
                    </div>
                    <p className="truncate text-[0.75rem] text-[var(--color-ink-muted)]">
                      {account?.handle ??
                        `${provider.charLimit} caracteres · ${provider.media.note}`}
                    </p>
                    {account?.lastError !== null && account?.lastError !== undefined && (
                      <p className="mt-1 text-[0.75rem] text-red-600">{account.lastError}</p>
                    )}
                  </div>
                  <Button size="sm" variant="secondary" onClick={() => setConnecting(provider)}>
                    {account === undefined ? "Conectar" : "Gerenciar"}
                  </Button>
                </Card>
              );
            })}
          </div>
        </section>
      ))}
      {connecting !== null && (
        <ConnectDialog
          provider={connecting}
          clientId={clientId}
          onClose={() => setConnecting(null)}
          onConnected={() => {
            setConnecting(null);
            onChanged();
          }}
          toast={toast}
        />
      )}
    </div>
  );
}

function defaultConnectMethod(provider: Provider): "oauth" | "credentials" {
  const methods = provider.connectMethods.filter((method) => method !== "sandbox");
  if (provider.oauthReady && methods.includes("oauth")) return "oauth";
  if (methods.includes("credentials")) return "credentials";
  return "oauth";
}

function ConnectDialog({
  provider,
  clientId,
  onClose,
  onConnected,
  toast,
}: {
  provider: Provider;
  clientId: string;
  onClose: () => void;
  onConnected: () => void;
  toast: ReturnType<typeof useToast>;
}) {
  const [method, setMethod] = useState<"oauth" | "credentials">(defaultConnectMethod(provider));
  const [displayName, setDisplayName] = useState(provider.name);
  const [credentials, setCredentials] = useState<Record<string, string>>({});

  const mutation = useMutation({
    mutationFn: async () => {
      const result = unwrap(
        await api.api.admin.social.accounts.post({
          platform: provider.id,
          method,
          clientId,
          displayName,
          credentials: method === "credentials" ? credentials : undefined,
        }),
      ) as { authorizeUrl?: string };
      if (typeof result.authorizeUrl === "string") {
        window.location.assign(result.authorizeUrl);
        return;
      }
    },
    onSuccess: () => {
      toast.success("Conta registrada", `${provider.name} conectada.`);
      onConnected();
    },
    onError: (error: unknown) =>
      toast.error("Não conectou", error instanceof ApiError ? error.message : "Tente novamente."),
  });

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title={`Conectar ${provider.name}`}
      description={
        provider.oauthReady
          ? "OAuth ao vivo usa o app de desenvolvedor configurado no servidor."
          : "Cole as credenciais da própria rede. Nada disso volta a ser exibido depois de salvo."
      }
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button onClick={() => void mutation.mutate()} disabled={mutation.isPending}>
            {mutation.isPending ? "Conectando…" : "Conectar"}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <Field label="Nome de exibição" htmlFor="connect-display-name">
          <Input
            id="connect-display-name"
            value={displayName}
            onChange={(event) => setDisplayName(event.target.value)}
          />
        </Field>
        <Field label="Método" htmlFor="connect-method">
          <Select
            id="connect-method"
            value={method}
            onChange={(event) => setMethod(event.target.value as typeof method)}
          >
            {provider.connectMethods.includes("credentials") && (
              <option value="credentials">Credenciais da rede</option>
            )}
            {provider.connectMethods.includes("oauth") && (
              <option value="oauth" disabled={!provider.oauthReady}>
                OAuth {provider.oauthReady ? "" : "(app não configurado no servidor)"}
              </option>
            )}
          </Select>
        </Field>
        {method === "credentials" &&
          provider.credentialFields.map((field) => (
            <Field
              key={field.key}
              label={field.label}
              hint={field.help}
              htmlFor={`connect-${field.key}`}
            >
              <Input
                id={`connect-${field.key}`}
                type={field.type === "password" ? "password" : "text"}
                required={field.required}
                placeholder={field.placeholder}
                value={credentials[field.key] ?? ""}
                onChange={(event) =>
                  setCredentials((current) => ({ ...current, [field.key]: event.target.value }))
                }
              />
            </Field>
          ))}
      </div>
    </Dialog>
  );
}

function ComposerPanel({
  providers,
  accounts,
  clientId,
  onPublished,
  toast,
}: {
  providers: Provider[];
  accounts: Account[];
  clientId: string;
  onPublished: () => void;
  toast: ReturnType<typeof useToast>;
}) {
  const byId = new Map(providers.map((p) => [p.id, p]));
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [mediaUrl, setMediaUrl] = useState("");
  const [selected, setSelected] = useState<string[]>([]);
  const [when, setWhen] = useState<"now" | "schedule" | "draft">("now");
  const [scheduledAt, setScheduledAt] = useState("");
  const [overrides, setOverrides] = useState<Record<string, Record<string, unknown>>>({});
  const [activeAccount, setActiveAccount] = useState<string | null>(null);

  const selectedAccounts = accounts.filter((a) => selected.includes(a.id));
  const active = selectedAccounts.find((a) => a.id === activeAccount) ?? selectedAccounts[0];
  const activeProvider = active === undefined ? undefined : byId.get(active.platform);

  const mutation = useMutation({
    mutationFn: async () => {
      const media =
        mediaUrl.trim() === ""
          ? []
          : [
              {
                url: mediaUrl.trim(),
                kind: mediaUrl.includes(".mp4") ? ("video" as const) : ("image" as const),
              },
            ];
      return unwrap(
        await api.api.admin.social.posts.post({
          clientId,
          title: title.trim() === "" ? content.slice(0, 80) || "Publicação" : title.trim(),
          globalContent: content,
          globalMedia: media,
          publishNow: when === "now",
          scheduledAt:
            when === "schedule" && scheduledAt !== "" ? new Date(scheduledAt).toISOString() : null,
          targets: selectedAccounts.map((account) => ({
            accountId: account.id,
            settings: { ...byId.get(account.platform)?.defaultSettings, ...overrides[account.id] },
          })),
        }),
      );
    },
    onSuccess: () => {
      toast.success(
        when === "now" ? "Publicado" : when === "schedule" ? "Agendado" : "Rascunho salvo",
      );
      setTitle("");
      setContent("");
      onPublished();
    },
    onError: (error: unknown) =>
      toast.error(
        "Não publicou",
        error instanceof ApiError ? error.message : "Revise as configurações.",
      ),
  });

  if (accounts.length === 0) {
    return (
      <EmptyState
        icon={<IconPublish className="size-5" />}
        title="Nenhuma rede conectada neste cliente"
        description="Abra a aba Contas e conecte uma rede com OAuth ou com as credenciais da própria plataforma."
      />
    );
  }

  return (
    <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_20rem]">
      <Card className="space-y-3 p-4">
        <Field label="Título interno" htmlFor="composer-title">
          <Input
            id="composer-title"
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            maxLength={200}
          />
        </Field>
        <Field
          label="Texto"
          htmlFor="composer-content"
          hint={
            activeProvider !== undefined
              ? `${content.length} / ${activeProvider.charLimit} em ${activeProvider.name}`
              : `${content.length} caracteres`
          }
        >
          <Textarea
            id="composer-content"
            rows={8}
            value={content}
            onChange={(event) => setContent(event.target.value)}
          />
        </Field>
        <Field
          label="URL de mídia (http/https)"
          htmlFor="composer-media"
          hint="Upload de arquivo ainda usa URL; o anexo S3 continua na lacuna documentada."
        >
          <Input
            id="composer-media"
            value={mediaUrl}
            onChange={(event) => setMediaUrl(event.target.value)}
            placeholder="https://cdn.exemplo.com/peca.jpg"
          />
        </Field>
        <div className="flex flex-wrap gap-3">
          <label className="flex items-center gap-2 text-sm">
            <input type="radio" checked={when === "now"} onChange={() => setWhen("now")} />
            Publicar agora
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="radio"
              checked={when === "schedule"}
              onChange={() => setWhen("schedule")}
            />
            Agendar
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input type="radio" checked={when === "draft"} onChange={() => setWhen("draft")} />
            Rascunho
          </label>
        </div>
        {when === "schedule" && (
          <Field label="Data e hora" htmlFor="composer-when">
            <Input
              id="composer-when"
              type="datetime-local"
              value={scheduledAt}
              onChange={(event) => setScheduledAt(event.target.value)}
            />
          </Field>
        )}
        <Button
          onClick={() => void mutation.mutate()}
          disabled={mutation.isPending || selectedAccounts.length === 0}
        >
          {mutation.isPending ? "Enviando…" : "Enviar"}
        </Button>
      </Card>

      <div className="space-y-3">
        <Card className="p-3">
          <p className="mb-2 text-sm font-semibold">Redes</p>
          <ul className="space-y-1">
            {accounts.map((account) => {
              const provider = byId.get(account.platform);
              const checked = selected.includes(account.id);
              return (
                <li key={account.id}>
                  <label className="flex cursor-pointer items-center gap-2 rounded-[var(--radius-sm)] px-1 py-1 text-sm transition-[background-color] duration-[var(--dream-duration-fast)] hover:bg-[var(--color-surface-muted)]">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => {
                        setSelected((current) =>
                          checked
                            ? current.filter((id) => id !== account.id)
                            : [...current, account.id],
                        );
                        setActiveAccount(account.id);
                      }}
                    />
                    <span
                      className="size-2.5 rounded-full"
                      style={{ backgroundColor: provider?.color ?? "#64748b" }}
                    />
                    <span className="truncate">{account.displayName}</span>
                  </label>
                </li>
              );
            })}
          </ul>
        </Card>

        {active !== undefined && activeProvider !== undefined && (
          <Card className="space-y-3 p-3">
            <p className="text-sm font-semibold">Configurações · {activeProvider.name}</p>
            <p className="text-[0.75rem] text-[var(--color-ink-muted)]">
              {activeProvider.media.note}
            </p>
            {activeProvider.settings.map((field) => {
              if (field.when !== undefined) {
                const current = (overrides[active.id] ?? activeProvider.defaultSettings)[
                  field.when.field
                ];
                if (String(current ?? "") !== String(field.when.equals)) return null;
              }
              const value = overrides[active.id]?.[field.key] ?? field.defaultValue ?? "";
              return (
                <div key={field.key} className="space-y-1">
                  <Label htmlFor={`${active.id}-${field.key}`}>{field.label}</Label>
                  {field.type === "boolean" ? (
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        id={`${active.id}-${field.key}`}
                        type="checkbox"
                        checked={Boolean(value)}
                        onChange={(event) =>
                          setOverrides((current) => ({
                            ...current,
                            [active.id]: {
                              ...current[active.id],
                              [field.key]: event.target.checked,
                            },
                          }))
                        }
                      />
                      {field.help}
                    </label>
                  ) : field.type === "select" ? (
                    <Select
                      id={`${active.id}-${field.key}`}
                      value={String(value)}
                      onChange={(event) =>
                        setOverrides((current) => ({
                          ...current,
                          [active.id]: { ...current[active.id], [field.key]: event.target.value },
                        }))
                      }
                    >
                      {(field.options ?? []).map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Select>
                  ) : field.type === "text" ? (
                    <Textarea
                      id={`${active.id}-${field.key}`}
                      rows={3}
                      value={String(value)}
                      onChange={(event) =>
                        setOverrides((current) => ({
                          ...current,
                          [active.id]: { ...current[active.id], [field.key]: event.target.value },
                        }))
                      }
                    />
                  ) : (
                    <Input
                      id={`${active.id}-${field.key}`}
                      type={field.type === "datetime" ? "datetime-local" : "text"}
                      value={String(value)}
                      maxLength={field.maxLength}
                      placeholder={field.placeholder}
                      onChange={(event) =>
                        setOverrides((current) => ({
                          ...current,
                          [active.id]: { ...current[active.id], [field.key]: event.target.value },
                        }))
                      }
                    />
                  )}
                  {field.help !== undefined && field.type !== "boolean" && (
                    <p className="text-[0.75rem] text-[var(--color-ink-muted)]">{field.help}</p>
                  )}
                </div>
              );
            })}
          </Card>
        )}
      </div>
    </div>
  );
}

function QueuePanel({
  posts,
  onChanged,
  toast,
}: {
  posts: SocialPost[];
  onChanged: () => void;
  toast: ReturnType<typeof useToast>;
}) {
  const publish = useMutation({
    mutationFn: async (id: string) =>
      unwrap(await api.api.admin.social.posts({ id }).publish.post()),
    onSuccess: () => {
      toast.success("Publicado");
      onChanged();
    },
    onError: (error: unknown) =>
      toast.error("Falha", error instanceof ApiError ? error.message : "Tente novamente."),
  });

  if (posts.length === 0) {
    return (
      <EmptyState
        icon={<IconPublish className="size-5" />}
        title="Fila vazia"
        description="As publicações compostas aparecem aqui."
      />
    );
  }

  return (
    <ul className="space-y-2">
      {posts.map((post) => (
        <li key={post.id}>
          <Card className="p-4">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div>
                <p className="font-medium text-[var(--color-ink)]">{post.title}</p>
                <p className="mt-0.5 line-clamp-2 text-[0.8125rem] text-[var(--color-ink-muted)]">
                  {post.globalContent || "—"}
                </p>
                {post.scheduledAt !== null && (
                  <p className="mt-1 text-[0.75rem] text-[var(--color-ink-muted)]">
                    {DateTime.fromISO(post.scheduledAt)
                      .setZone("America/Fortaleza")
                      .toFormat("dd/LL HH:mm")}
                  </p>
                )}
              </div>
              <Badge
                tone={
                  post.status === "published"
                    ? "success"
                    : post.status === "failed"
                      ? "danger"
                      : post.status === "scheduled"
                        ? "info"
                        : "neutral"
                }
              >
                {SOCIAL_POST_STATUS_LABELS[post.status]}
              </Badge>
            </div>
            <ul className="mt-3 flex flex-wrap gap-2">
              {post.targets.map((target) => (
                <li key={target.id}>
                  <Badge
                    tone={
                      target.status === "published"
                        ? "success"
                        : target.status === "failed"
                          ? "danger"
                          : "neutral"
                    }
                  >
                    {target.displayName ?? target.platform} · {target.status}
                  </Badge>
                </li>
              ))}
            </ul>
            {(post.status === "draft" ||
              post.status === "scheduled" ||
              post.status === "failed" ||
              post.status === "partial") && (
              <div className="mt-3">
                <Button
                  size="sm"
                  onClick={() => void publish.mutate(post.id)}
                  disabled={publish.isPending}
                >
                  Publicar agora
                </Button>
              </div>
            )}
          </Card>
        </li>
      ))}
    </ul>
  );
}
